// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import constants from "@store/constants";
import asString from "@store/Framework/Common/Utilities/asString";
import { InviteYourColleagueApiParameter } from "@store/Framework/Services/InviteYourColleagueService";
import clearForm from "@store/Framework/Store/Greif/Components/InviteYourColleagueForm/Handlers/ClearForm";
import submitInviteYourColleagueForm from "@store/Framework/Store/Greif/Components/InviteYourColleagueForm/Handlers/SubmitInviteYourColleagueForm";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import React, { createRef, FormEvent, RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    submitButtonLabel = "submitButtonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonLabel]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
    pageTitle: getCurrentPage(state).fields.title,
    inviteYourColleagueForm: state.greif.components.inviteYourColleagueForm,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    clearForm,
    submitInviteYourColleagueForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InviteYourColleagueFormStyles {
    gridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const inviteYourColleagueFormStyles: InviteYourColleagueFormStyles = {
    gridContainer: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
};

const styles = inviteYourColleagueFormStyles;

type Validator = () => boolean;

type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const InviteYourColleagueFormContext = React.createContext<ContextType>({
    validators: {},
});

const InviteYourColleagueForm = ({
    id,
    fields,
    inviteYourColleagueForm,
    session,
    clearForm,
    submitInviteYourColleagueForm,
}: Props) => {
    const { userLabel } = session;
    const { fieldValues } = inviteYourColleagueForm;
    const { submitButtonLabel } = fields;

    const { h } = useQueryString();

    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = createRef();

    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });

        const results = widgetValidators.map(v => v());

        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = validateReCaptcha({
            location: "InviteYourColleague",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        const formData = {
            inviteYourColleagueModel: {
                requestor: userLabel,
                firstName: fieldValues["firstName"],
                lastName: fieldValues["lastName"],
                email: fieldValues["email"],
                billTo: asString(fieldValues["soldTo"]) || undefined,
                shipTo: asString(fieldValues["shipTo"]) || undefined,
                jobTitle: asString(fieldValues["jobTitle"]) || undefined,
                portalInfo: fieldValues["portalInfo"],
                userHash: h || "",
            },
        } as InviteYourColleagueApiParameter;

        setIsSubmitting(true);

        submitInviteYourColleagueForm({
            ...formData,
            onSuccess: () => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: siteMessage("InviteYourColleague_FormSubmitSuccess"),
                    messageType: "success",
                    timeoutLength: 3000,
                });
                clearForm();
            },
            onError: () => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: siteMessage("InviteYourColleague_FormSubmitError"),
                    messageType: "danger",
                    timeoutLength: 3000,
                });
            },
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.formGridItem}>
                <InviteYourColleagueFormContext.Provider value={{ validators }}>
                    <form id="inviteYourColleagueForm" onSubmit={handleFormSubmit} noValidate>
                        <LoadingOverlay loading={isSubmitting}>
                            <Zone contentId={id} zoneName="Content" />
                        </LoadingOverlay>
                        <FormActions>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting}
                                data-test-selector="submit-button"
                            >
                                {submitButtonLabel}
                            </Button>
                        </FormActions>
                    </form>
                </InviteYourColleagueFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InviteYourColleagueForm),
    definition: {
        group: constants.widgetGroup.inviteYourColleague,
        displayName: "Form",
        allowedContexts: [constants.pageType.inviteYourColleague, constants.pageType.notificationPreferences],
        fieldDefinitions: [
            {
                name: fields.submitButtonLabel,
                displayName: "Submit Button Label",
                editorTemplate: "TextField",
                defaultValue: "Send Invite",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
